import { Pagination, Table } from 'antd';
import Column from 'antd/es/table/Column';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import {
  DeleteActionSvg,
  EditActionSvg,
  EyeIcon,
  SortBottomIcon,
  SortTopIcon,
  TableNextIcon,
  TablePreviousIcon,
} from 'utils/svgs';
import { checkPermission } from 'utils/utils';
import SearchAndFilter from '../SearchAndFilter';
import styles from '../../Medicines/CustomTable/index.module.scss';
import './index.scss';
import { CustomGroceryTableProps } from './type';

const renderWrappedCell = (value: string | null | undefined) => (
  <span className="grocery-table-cell-wrap">{value || '-'}</span>
);

const CustomTable: FC<CustomGroceryTableProps> = (props) => {
  const {
    dataSource,
    handleTableChange,
    onShowSizeChange,
    isLoading,
    search,
    totalRecords,
    performSearchHandler,
    currentPage,
    pageSize,
    setSearch,
    viewHandler,
    openDrawerHandler,
    setEditSelectedGrocery,
    setTitle,
    showModal,
    statusFilter,
    onStatusFilterChange,
    selectedDate,
    setSelectedDate,
    allUsers,
    selectedUser,
    setSelectedUser,
  } = props;

  const userPermissions = useSelector((state: any) => state?.userProfile);

  const isViewPermission = checkPermission(userPermissions?.data?.permissions, {
    permissionName: 'GroceryItems',
    permission: ['view', 'viewAll'],
  });

  const isEditPermission = checkPermission(userPermissions?.data?.permissions, {
    permissionName: 'GroceryItems',
    permission: ['edit'],
  });

  const isImageEditPermission = checkPermission(userPermissions?.data?.permissions, {
    permissionName: 'Images',
    permission: ['edit'],
  });

  const canEdit = isEditPermission || isImageEditPermission;

  const isDeletePermission = checkPermission(userPermissions?.data?.permissions, {
    permissionName: 'GroceryItems',
    permission: ['delete'],
  });

  const editDataHandler = (item: any) => {
    openDrawerHandler();
    setTitle('Edit Grocery');
    setEditSelectedGrocery(item);
  };

  const deleteDataHandler = (item: any) => {
    setEditSelectedGrocery(item);
    showModal();
  };

  return (
    <>
      <div className={`${styles['body']} user-table`}>
        <SearchAndFilter
          performSearchHandler={performSearchHandler}
          searchValue={search}
          openDrawerHandler={openDrawerHandler}
          title="Grocery List"
          setSearch={setSearch}
          totalRecords={totalRecords}
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilterChange}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          allUsers={allUsers}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
        />

        <Table
          rowKey={(record: any) => record?.id}
          dataSource={dataSource}
          pagination={false}
          loading={isLoading}
          onChange={handleTableChange}
          tableLayout="fixed"
          scroll={{ y: 'calc(100vh - 300px)' }}
        >
          <Column
            title={<p style={{ padding: '0 10px' }}>Product Name</p>}
            dataIndex="productName"
            key="productName"
            className="user-column bg-white grocery-wrap-col"
            width="14%"
            sorter
            sortDirections={['ascend', 'descend', 'ascend']}
            render={renderWrappedCell}
            sortIcon={({ sortOrder }) => (
              <>
                <SortTopIcon fill={sortOrder === 'ascend' ? '#949494' : undefined} />
                <SortBottomIcon fill={sortOrder === 'descend' ? '#949494' : undefined} />
              </>
            )}
          />
          <Column
            title="Brand"
            dataIndex="brand"
            key="brand"
            className="bg-white grocery-wrap-col"
            width="9%"
            render={renderWrappedCell}
          />
          <Column
            title="Manufacturer"
            dataIndex="manufacturerName"
            key="manufacturerName"
            className="bg-white grocery-wrap-col"
            width="11%"
            render={renderWrappedCell}
          />
          <Column
            title="MRP"
            dataIndex="mrp"
            key="mrp"
            className="bg-white"
            width="7%"
            render={(value: number) => (value != null ? value : '-')}
          />
          <Column
            title="Pack Type"
            dataIndex="packType"
            key="packType"
            className="bg-white grocery-wrap-col"
            width="8%"
            sorter
            sortDirections={['ascend', 'descend', 'ascend']}
            render={renderWrappedCell}
            sortIcon={({ sortOrder }) => (
              <>
                <SortTopIcon fill={sortOrder === 'ascend' ? '#949494' : undefined} />
                <SortBottomIcon fill={sortOrder === 'descend' ? '#949494' : undefined} />
              </>
            )}
          />
          <Column
            title="Category"
            dataIndex="categoryName"
            key="categoryName"
            className="bg-white grocery-wrap-col"
            width="10%"
            render={renderWrappedCell}
          />
          <Column
            title="Image"
            dataIndex="image"
            key="image"
            className="bg-white"
            width="6%"
            align="center"
            render={(_: unknown, record: any) => {
              const hasPrimary =
                record?.productPrimaryImageUrl &&
                String(record.productPrimaryImageUrl).trim() !== '';
              const hasSecondary =
                Array.isArray(record?.productSecondaryImageUrl) &&
                record.productSecondaryImageUrl.length > 0;
              if (hasPrimary || hasSecondary) {
                return <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />;
              }
              return <CloseCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />;
            }}
          />
          <Column
            title="Quality Check"
            dataIndex="qualityCheck"
            key="qualityCheck"
            className="bg-white"
            width="8%"
            render={(value: string) => {
              if (!value) return '-';
              const normalized = value.toUpperCase();
              const statusClass =
                normalized === 'PENDING'
                  ? styles.status__pending
                  : normalized === 'REJECTED'
                    ? styles.status__rejected
                    : styles.status__approved;
              return <span className={statusClass}>{value}</span>;
            }}
          />
          {(isViewPermission || canEdit || isDeletePermission) && (
            <Column
              title="Action"
              dataIndex="action"
              key="action"
              className="bg-white grocery-table-action-col"
              width="11%"
              align="center"
              render={(_: unknown, data: any) => (
                <div className="grocery-table-actions">
                  {isViewPermission ? (
                    <div
                      className="cursor-pointer flex align-center justify-center"
                      onClick={() => viewHandler(data)}
                    >
                      <EyeIcon />
                    </div>
                  ) : (
                    <div className="cursor-not-allowed flex align-center justify-center">
                      <EyeIcon />
                    </div>
                  )}

                  {canEdit ? (
                    <div
                      className="cursor-pointer flex align-center justify-center rounded-action-btn"
                      onClick={() => editDataHandler(data)}
                    >
                      <EditActionSvg />
                    </div>
                  ) : (
                    <div className="cursor-not-allowed flex align-center justify-center rounded-action-btn">
                      <EditActionSvg />
                    </div>
                  )}

                  {isDeletePermission ? (
                    <div
                      className="cursor-pointer flex align-center justify-center rounded-action-btn"
                      onClick={() => deleteDataHandler(data)}
                    >
                      <DeleteActionSvg />
                    </div>
                  ) : (
                    <div className="cursor-not-allowed flex align-center justify-center rounded-action-btn">
                      <DeleteActionSvg />
                    </div>
                  )}
                </div>
              )}
            />
          )}
        </Table>

        <div className={styles['pagination__wrapper']}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            showSizeChanger
            onChange={onShowSizeChange}
            total={totalRecords}
            itemRender={(page, type, originalElement) => (
              <div className={styles['pagination']}>
                {type === 'prev' ? (
                  <button
                    type="button"
                    className={`${styles['pagination']} ${styles['pagination__button']}`}
                  >
                    <TablePreviousIcon />
                    <p className={styles['pagination__previous']}>Previous</p>
                  </button>
                ) : type === 'next' ? (
                  <button
                    type="button"
                    className={`${styles['pagination']} ${styles['pagination__button']}`}
                  >
                    <p className={styles['pagination__next']}>Next</p>
                    <TableNextIcon />
                  </button>
                ) : (
                  originalElement
                )}
              </div>
            )}
          />
        </div>
      </div>
    </>
  );
};

export default CustomTable;
